import type { SignedPortalHandoffClaim } from "~portal/helpers/portalHandoffState";

import { generateVerifierAndChallenge } from "~/auth/PKCEClient";
import Config from "~/config";
import logger from "~/helpers/logger";
import { RECENT_DESTINATIONS_KEY } from "~/helpers/recentDestinations";
import { purgePersistedStore } from "~/helpers/tenantSwitchCleanup";
import isBenchmarkHost from "~/pwa/benchmarkHost";
import { buildCanonicalPortalLoginUrl } from "~portal/helpers/canonicalPortalHost";
import classifyHost from "~portal/helpers/hostClassification";
import {
	createPortalHandoffClaim,
	encodePortalHandoffClaim,
	signPortalHandoffClaim
} from "~portal/helpers/portalHandoffState";
import resolvePortalLoginHint from "~portal/helpers/resolvePortalLoginHint";

const authUrl = Config.authUrl;
const clientId = Config.clientId;
const tenantId = Config.tenantId;

export const OAUTH_STATE_HASH_KEY = "oauth_state_hash";

/** Hash a string using SHA-256 and return a hex-encoded digest. */
export async function sha256Hex(input: string): Promise<string> {
	const data = new TextEncoder().encode(input);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Sanitize a returnTo path to only allow relative paths starting with "/".
 * Rejects absolute URLs, protocol-relative URLs, and anything that doesn't
 * start with a single "/". Returns "/" as the default for invalid values.
 */
export function sanitizeReturnTo(returnTo: unknown): string {
	if (typeof returnTo !== "string" || !returnTo) {
		return "/";
	}

	// Reject protocol-relative URLs (//...) and absolute URLs (http://, https://, etc.)
	if (returnTo.startsWith("//") || /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(returnTo)) {
		return "/";
	}

	// Must start with a single "/"
	if (!returnTo.startsWith("/")) {
		return "/";
	}

	return returnTo;
}

export class SFAuth {
	private static _instance: SFAuth;

	private currentAccessToken: Maybe<string> = null;
	private currentIdToken: Maybe<string> = null;
	private currentRefreshToken: Maybe<string> = null;

	// Singleton
	private constructor() {}

	public static get Instance() {
		if (!this._instance) {
			this._instance = new this();
		}
		return this._instance;
	}

	async checkSession(forceRenewToken?: boolean): Promise<string> {
		const tokens = this.getCurrentTokens();
		if (tokens === null) {
			return "";
		}

		try {
			const freshTokens = await this.refreshPKCESessionIfNeeded(tokens, forceRenewToken || false);

			this.currentAccessToken = freshTokens.accessToken;
			this.currentRefreshToken = freshTokens.refreshToken;
			// Preserve existing idToken through refresh (refresh endpoint doesn't return one)
			if (freshTokens.idToken) {
				this.currentIdToken = freshTokens.idToken;
			}
			const serializedTokens = JSON.stringify({ ...freshTokens, idToken: this.currentIdToken });
			if (window.localStorage.getItem("appTokens") !== serializedTokens) {
				window.localStorage.setItem("appTokens", serializedTokens);
			}
			return freshTokens.accessToken;
		} catch {
			return "";
		}
	}

	/** Return current tokens (public accessor for hooks that need to decode the JWT). */
	getTokens(): Maybe<TokenSet> {
		return this.getCurrentTokens();
	}

	/** Return current tokens stored in memory if available. Else retrieve from
	 * localStorage. If not available, return null. */
	private getCurrentTokens(): Maybe<TokenSet> {
		let tokens: Maybe<TokenSet> = null;

		if (this.currentAccessToken && this.currentRefreshToken) {
			tokens = {
				accessToken: this.currentAccessToken,
				idToken: this.currentIdToken ?? undefined,
				refreshToken: this.currentRefreshToken
			};
		} else {
			const tokenStr = window.localStorage.getItem("appTokens");
			if (tokenStr) {
				try {
					tokens = JSON.parse(tokenStr);
				} catch {
					logger.warn("Corrupted token data in localStorage, clearing appTokens");
					window.localStorage.removeItem("appTokens");
					tokens = null;
				}
			}
		}

		return tokens;
	}

	/** Returns a `Promise<boolean>` for whether or not valid tokens are stored (transparently renews
	 * access token if it is at/near expiry) */
	private async refreshPKCESessionIfNeeded(tokens: TokenSet, forceRenewToken?: boolean): Promise<TokenSet> {
		let freshTokens = { ...tokens };

		// Refresh if access token expired
		if (this.isTokenNearExpiry(tokens.accessToken) || forceRenewToken || false) {
			freshTokens = await this.refreshToken(tokens.accessToken, tokens.refreshToken);
		}

		return freshTokens;
	}

	private async refreshToken(accessToken: string, refreshToken: string): Promise<JWTRefreshResponse> {
		const url = `${authUrl}/api/jwt/refresh`;

		const response = await fetch(url, {
			body: JSON.stringify({
				refreshToken,
				token: accessToken
			}),
			headers: {
				"Content-Type": "application/json"
			},
			method: "POST"
		});

		if (!response.ok) {
			throw new Error(response.statusText);
		}

		const data = await response.json();
		return {
			accessToken: data.token,
			refreshToken: data.refreshToken
		};
	}

	parseTokenPayload(accessToken: string) {
		const base64Url = accessToken.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split("")
				.map(function (c) {
					return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join("")
		);

		return JSON.parse(jsonPayload);
	}

	// Return true if access token is within `thresholdPercentage` of expiry relative to token lifetime
	// eg if thresholdPercentage is 20, and token lifetime is 3600 seconds, then if the token has less than
	// 720 seconds (20% of 3600 seconds) remaining, it is considered near expiry.
	isTokenNearExpiry(accessToken: string, thresholdPercentage: number = 20): boolean {
		const payload = this.parseTokenPayload(accessToken);
		// expiry and issued at timestamps in seconds
		const { exp, iat } = payload;

		const currentTime = Date.now() / 1000; // Convert to seconds
		const timeRemaining = exp - currentTime;
		const tokenLifetime = exp - iat; // Calculate token lifetime

		// Calculate the threshold time as a percentage of token lifetime
		const thresholdTime = (thresholdPercentage / 100) * tokenLifetime;

		// Check if the remaining time is less than or equal to the threshold time
		const nearExpiry = timeRemaining <= thresholdTime;

		return nearExpiry;
	}
	async exchangePKCECodeForTokens(code: string): Promise<void> {
		const verifier = window.localStorage.getItem("pkceVerifier");
		if (verifier === null) {
			throw new Error("No PKCE verifier found.");
		}

		const data = new URLSearchParams();
		data.append("client_id", clientId);
		data.append("code", code);
		data.append("code_verifier", verifier);
		data.append("grant_type", "authorization_code");
		// For PKCE, redirect is directly back to app origin
		data.append("redirect_uri", `${window.location.origin}/oauth-redirect`);

		const response = await fetch(`${authUrl}/oauth2/token`, {
			body: data,
			headers: {
				"Content-Type": "application/x-www-form-urlencoded"
			},
			method: "POST"
		});

		if (response.status !== 200) {
			throw new Error("Error exchanging code for tokens");
		}

		const tokensResponse = await response.json();
		const tokens: TokenSet = {
			accessToken: tokensResponse.access_token,
			idToken: tokensResponse.id_token,
			refreshToken: tokensResponse.refresh_token
		};

		this.storeSessionTokens(tokens);
		window.localStorage.removeItem("pkceVerifier");
		return;
	}

	storeSessionTokens(tokens: TokenSet): void {
		window.localStorage.setItem("appTokens", JSON.stringify(tokens));
		this.currentAccessToken = tokens.accessToken;
		this.currentIdToken = tokens.idToken ?? null;
		this.currentRefreshToken = tokens.refreshToken;
	}

	clearLocalSession(): void {
		// Order matters: null tokens, then remove session-bound storage. Skip Redux
		// LOGOUT dispatch — RTK Query refetch races storage clear and can silently
		// re-auth via checkSession(). Targeted removal (not localStorage.clear)
		// preserves user prefs; add new session-bound user-data keys here.
		this.currentAccessToken = null;
		this.currentIdToken = null;
		this.currentRefreshToken = null;
		window.localStorage.removeItem("appTokens");
		window.localStorage.removeItem("pkceVerifier");
		window.localStorage.removeItem("worksuite-ai-chats");
		window.localStorage.removeItem(RECENT_DESTINATIONS_KEY);
		window.sessionStorage.removeItem(OAUTH_STATE_HASH_KEY);
		window.sessionStorage.removeItem("loadListSessionId");
	}

	private async redirectToCanonicalPortalLogin(domain: string, onSuccessReturnToPath: boolean): Promise<void> {
		const claim = createPortalHandoffClaim(domain, onSuccessReturnToPath ? window.location.pathname : "/");
		const signed = await signPortalHandoffClaim(claim);
		window.location.href = buildCanonicalPortalLoginUrl(encodePortalHandoffClaim(signed));
	}

	async redirectToLogin(
		onSuccessReturnToPath = true,
		portalHandoff?: SignedPortalHandoffClaim,
		options?: RedirectToLoginOptions
	) {
		// Just came back from login attempt, don't redirect again
		if (window.location.pathname.startsWith("/oauth-redirect")) {
			return;
		}

		if (!portalHandoff) {
			const classification = classifyHost(window.location.host);
			if (classification.kind === "customDomainCandidate") {
				await this.redirectToCanonicalPortalLogin(classification.host, onSuccessReturnToPath);
				return;
			}
		}

		const queryparams: Record<string, string> = {
			client_id: clientId,
			// redirect_uri: `${redirectUri}${port ? `:${port}` : ''}/oauth-redirect`,
			redirect_uri: `${window.location.origin}/oauth-redirect`,
			response_type: "code",
			scope: "openid profile email offline_access"
		};
		if (options?.prompt) {
			queryparams.prompt = options.prompt;
		}

		const authState = {
			// Not used with PKCE
			appName: undefined,

			// ** Optional **
			// On dev, a port can be provided so that the redirect returns the user
			// back to the correct port after successful authentication.
			port: window.location.port,

			// Undefined outside the custom-domain flow, so JSON.stringify omits it and the state is unchanged.
			portalHandoff,

			portal: resolvePortalLoginHint(window.location.host, window.location.pathname, portalHandoff) ?? undefined,

			// ** Optional **
			// By default, return to current path after authentication; else return to root
			returnTo: onSuccessReturnToPath ? `${window.location.pathname}${window.location.search}` : "/"
		};

		const { challenge, verifier } = await generateVerifierAndChallenge();
		window.localStorage.setItem("pkceVerifier", verifier);
		queryparams.code_challenge_method = "S256";
		queryparams.code_challenge = challenge;
		const stateStr = JSON.stringify(authState);
		queryparams.state = stateStr;

		// Store a SHA-256 hash of the state parameter for CSRF validation on callback
		const stateHash = await sha256Hex(stateStr);
		window.sessionStorage.setItem(OAUTH_STATE_HASH_KEY, stateHash);

		const params = new URLSearchParams(queryparams);
		const qs = params.toString();
		window.location.href = `${authUrl}/oauth2/authorize?${qs}`;
	}

	redirectToLogout() {
		const params = new URLSearchParams({
			client_id: clientId,
			state: JSON.stringify({
				// ** Optional **
				// On dev, a port can be provided so that the redirect returns the user
				// back to the correct port after logging out.
				port: window.location.port
			}),
			tenantId: tenantId
		});
		if (isBenchmarkHost(window.location.host)) {
			params.set("post_logout_redirect_uri", `${window.location.origin}/`);
		}
		const qs = params.toString();
		const logoutUrl = `${authUrl}/oauth2/logout?${qs}`;

		this.clearLocalSession();

		// Same underlying purge TenantSwitcher's purgeTenantScopedState() calls, fire-and-forget like the previous localforage.clear().
		void purgePersistedStore();

		// Dynamic import to avoid a circular dep with subscriptionClient → sfAuth
		import("~/services/subscriptionClient").then(({ disposeSubscriptionClient }) => {
			void disposeSubscriptionClient();
		});

		window.location.href = logoutUrl;
	}
}

const sfAuth = SFAuth.Instance;

export default sfAuth;
